import { Link } from "@tanstack/react-router";

import AwardIcon from "@/components/sidebar/icons/award.svg?react";
import BriefcaseIcon from "@/components/sidebar/icons/briefcase.svg?react";
import FolderIcon from "@/components/sidebar/icons/folder.svg?react";
import LayersIcon from "@/components/sidebar/icons/layers-two.svg?react";
import UserIcon from "@/components/sidebar/icons/user.svg?react";

import type {
  GlobalSearchResponse,
  GlobalSearchResponseKey,
  SearchResultItem,
} from "../types";

import {
  isBrandResult,
  isMyFilesResult,
  isProjectResult,
  isRequestResult,
  isUserResult,
} from "../types";

type SearchResultSectionProps = {
  results: GlobalSearchResponse[keyof GlobalSearchResponse];
  title: string;
  type: GlobalSearchResponseKey;
};

const SECTION_ICONS: Record<
  GlobalSearchResponseKey,
  React.ComponentType<{ className?: string }>
> = {
  brands: AwardIcon,
  myFiles: FolderIcon,
  projects: BriefcaseIcon,
  requests: LayersIcon,
  users: UserIcon,
};

export function SearchResultSection({
  results,
  title,
  type,
}: SearchResultSectionProps) {
  if (results?.length === 0 || !results) return null;

  const Icon = SECTION_ICONS[type];

  return (
    <div className="flex flex-col text-sm">
      <div className="flex items-center gap-sm px-xl py-lg">
        <Icon className="size-4 text-text-quaternary" />
        <span className="text-xs font-semibold uppercase text-text-quaternary">
          {title}
        </span>
      </div>
      <div className="flex flex-col">
        {results.map((result) =>
          renderResultContent(result as SearchResultItem, type),
        )}
      </div>
    </div>
  );
}

function renderResultContent(
  result: SearchResultItem,
  type: GlobalSearchResponseKey,
) {
  if (isUserResult(result) && type === "users") {
    return (
      <Link
        className="flex select-none flex-col gap-xs px-xl py-md hover:bg-utility-gray-200"
        key={result.id}
        params={{ userId: String(result.id) }}
        to="/users/$userId/edit"
      >
        <span className="line-clamp-1 font-semibold text-text-secondary">
          {result.name}
        </span>
        {result.email && (
          <span className="line-clamp-1 text-text-tertiary">
            {result.email}
          </span>
        )}
      </Link>
    );
  }

  if (isRequestResult(result) && type === "requests") {
    return (
      <Link
        className="flex select-none flex-col gap-xs px-xl py-md hover:bg-utility-gray-200"
        key={result.id}
        params={{ requestId: result.id }}
        to="/requests/$requestId/edit"
      >
        <span className="line-clamp-1 font-semibold text-text-secondary">
          {result.name}
        </span>
        <span className="line-clamp-1 text-text-tertiary">
          {result.project.name}
        </span>
      </Link>
    );
  }

  if (isBrandResult(result) && type === "brands") {
    return (
      <Link
        className="flex select-none flex-col gap-xs px-xl py-md hover:bg-utility-gray-200"
        key={result.id}
        params={{ brandId: String(result.id) }}
        to="/brands/$brandId/overview"
      >
        <span className="line-clamp-1 font-semibold text-text-secondary">
          {result.name}
        </span>
        <span className="line-clamp-1 text-text-tertiary">
          {result.project.name}
        </span>
      </Link>
    );
  }

  if (isProjectResult(result) && type === "projects") {
    return (
      <Link
        className="flex select-none flex-col gap-xs px-xl py-md hover:bg-utility-gray-200"
        key={result.id}
        params={{ projectId: String(result.id) }}
        to="/projects/$projectId"
      >
        <span className="line-clamp-1 font-semibold text-text-secondary">
          {result.name}
        </span>
      </Link>
    );
  }

  if (isMyFilesResult(result) && type === "myFiles") {
    const params = new URLSearchParams({
      brandId: result.brand.id.toString(),
      projectId: result.project.id.toString(),
      requestId: result.requestId.toString(),
      requestType: result.requestType,
    });

    if (result.folder) {
      params.append("folder", result.folder);
    }

    return (
      <a
        className="flex select-none flex-col gap-xs px-xl py-md hover:bg-utility-gray-200"
        href={`/my-files?${params.toString()}`}
        key={result.id}
      >
        <span className="line-clamp-1 font-semibold text-text-secondary">
          {result.name}
        </span>
        <span className="line-clamp-1 text-text-tertiary">
          {result.project.name}
        </span>
      </a>
    );
  }
}
